import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Button, Paper, FormControlLabel, Checkbox, Grid,
} from '@mui/material';
import { shopActions } from '../../../redux/systemSetting/shop/shopState';
import DataGrid from '../../../components/templates/DataGrid';
import { FormTitle, SuccessSnackbar } from '../../../components/atoms/Base';

const DEFAULT_FORM = {
  id: '',
  name: '',
  receptionStartTime: '',
  receptionEndTime: '',
  baseRegularHolidays: [],
  regularHolidays: [],
};

const dayOfWeeks = [
  { id: 1, label: '月' },
  { id: 2, label: '火' },
  { id: 3, label: '水' },
  { id: 4, label: '木' },
  { id: 5, label: '金' },
  { id: 6, label: '土' },
  { id: 0, label: '日' },
  { id: 7, label: '祝' },
];

export default function ShopDetailInfoCard(props) {
  const { shopId } = props;
  const dispatch = useDispatch();
  const dbShop = useSelector((state) => state.shopStore.data);
  const isSaveSuccess = useSelector((state) => state.shopStore.isSaveSuccess);

  const [isSuccessOpen, setSuccessOpen] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState('');
  const [isEdit, setEdit] = React.useState(false);
  const [form, setForm] = React.useState(DEFAULT_FORM);
  const [shopData, setShopData] = React.useState({});

  const search = () => {
    dispatch(shopActions.getData({ id: shopId }));
  };

  React.useEffect(() => {
    if (dbShop) {
      const base = dbShop.regularHolidays ?? [];
      setForm({
        ...dbShop.data,
        baseRegularHolidays: base,
        regularHolidays: base,
      });
      setShopData({
        ...dbShop.data,
        regularHolidays: base,
      });
    }
  }, [dbShop]);

  React.useEffect(() => {
    if (isSaveSuccess) {
      search();
      setSuccessOpen(true);
      setSuccessMessage('保存しました');
      dispatch(shopActions.resetFlgs());
    }
  }, [isSaveSuccess]);

  const onEdit = () => {
    search();
    setEdit(true);
  };

  const onClose = () => {
    setEdit(false);
    // キャンセル時は「編集前（DBの状態）」に戻す
    setForm((prev) => ({
      ...prev,
      ...shopData,
      baseRegularHolidays: shopData.regularHolidays ?? [],
      regularHolidays: shopData.regularHolidays ?? [],
    }));
  };

  const onSuccessSnackbarClose = () => {
    setSuccessOpen(false);
    setSuccessMessage('');
  };

  const onClick = (_event, dayOfWeekId) => {
    setForm((prev) => {
      const baseList = prev.baseRegularHolidays ?? [];
      const currentList = prev.regularHolidays ?? [];

      const index = currentList.findIndex((h) => h.dayOfWeek === dayOfWeekId);
      const nextList = [...currentList];

      if (index < 0) {
        const base = baseList.find((h) => h.dayOfWeek === dayOfWeekId);
        nextList.push(base ?? { shopId, dayOfWeek: dayOfWeekId });
      } else {
        nextList.splice(index, 1);
      }

      return {
        ...prev,
        regularHolidays: nextList,
      };
    });
  };

  const onSave = () => {
    dispatch(shopActions.updateData({
      id: form.id,
      receptionStartTime: form.receptionStartTime,
      receptionEndTime: form.receptionEndTime,
      regHolidayList: form.regularHolidays,
      updatedAt: form.updatedAt,
    }));
    setEdit(false);
    onClose();
  };

  const viewHolidayInfo = () => {
    if (!isEdit) {
      let holidayStr = '';
      dayOfWeeks.forEach((dayOfWeek) => {
        const isChecked = (form.baseRegularHolidays ?? []).some(
          (holiday) => holiday.dayOfWeek === dayOfWeek.id,
        );
        if (isChecked) {
          if (holidayStr) holidayStr += '、';
          holidayStr += dayOfWeek.label;
        }
      });
      return holidayStr;
    }

    return dayOfWeeks.map((dayOfWeek) => {
      const isChecked = (form.regularHolidays ?? []).some(
        (holiday) => holiday.dayOfWeek === dayOfWeek.id,
      );
      return (
        <FormControlLabel
          control={(
            <Checkbox
              checked={isChecked}
              onChange={(event) => onClick(event, dayOfWeek.id)}
              name="screenName"
            />
          )}
          label={dayOfWeek.label}
          key={dayOfWeek.label}
        />
      );
    });
  };

  return (
    <>
      <Box
        mr={1}
        style={{
          minHeight: '48px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        {!isEdit && (
          <Button variant="contained" size="small" onClick={onEdit}>
            編集
          </Button>
        )}
      </Box>

      <Paper elevation={3} style={{ padding: '10px' }}>
        <DataGrid title="店舗名" value={shopData.name} />
        <DataGrid title="営業開始時間" value={shopData.receptionStartTime} />
        <DataGrid title="営業終了時間" value={shopData.receptionEndTime} />

        <Grid container spacing={1} mb={2} style={{ wordBreak: 'break-all' }}>
          <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
            <FormTitle title="定休日" />
          </Grid>
          <Grid item xs={12} md={8}>
            <Box>
              {viewHolidayInfo()}
            </Box>
          </Grid>
        </Grid>

        {isEdit && (
          <Box
            position="sticky"
            bottom={0}
            left={0}
            textAlign="center"
            width="100%"
            p={1}
            backgroundColor="#fff"
            borderTop="1px solid rgba(0, 0, 0, .125)"
          >
            <Button
              color="inherit"
              variant="contained"
              size="small"
              onClick={onClose}
              style={{ marginRight: '10px' }}
            >
              キャンセル
            </Button>
            <Button variant="contained" size="small" onClick={onSave}>
              保存
            </Button>
          </Box>
        )}
      </Paper>

      <SuccessSnackbar
        open={isSuccessOpen}
        onClose={onSuccessSnackbarClose}
        message={successMessage}
      />
    </>
  );
}
